import { Component, EventEmitter, Input, OnDestroy, Output } from "@angular/core";
import { Subject, combineLatest } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { PublishStateService } from "app/js/src/infrastructure/clipping-studio/publish-state.service";
import { SocialAccount } from "app/js/src/domain/clipping-studio/publish/publish-models";

/**
 * Destination dropdown for Meta-Data / Thumbnail: "All destinations" plus
 * only destinations currently selected on the Destinations tab.
 */
@Component({
  selector: "evrideo-clipping-studio-destination-picker",
  templateUrl: "./clipping-studio-destination-picker.component.html",
  styleUrls: ["./clipping-studio-destination-picker.component.scss"],
})
export class ClippingStudioDestinationPickerComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  /** Checked destinations only (for dropdown options). */
  destinations: SocialAccount[] = [];

  private _selectedDestinationId: string | null = null;

  @Input()
  get selectedDestinationId(): string | null {
    return this._selectedDestinationId;
  }
  set selectedDestinationId(value: string | null) {
    this._selectedDestinationId = value?.trim() || null;
  }

  @Output() selectedDestinationIdChange = new EventEmitter<string | null>();

  constructor(private publishState: PublishStateService) {
    combineLatest([
      this.publishState.destinations$,
      this.publishState.selectedDestinations$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([, selectedIds]) => {
        const selected = new Set(selectedIds ?? []);
        this.destinations = this.publishState
          .getDestinationsForUi()
          .filter((d) => selected.has(d.id));
        this.ensureSelectionIsAvailable();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDropdownChange(value: string | null): void {
    const next = value?.trim() || null;
    this._selectedDestinationId = next;
    this.selectedDestinationIdChange.emit(next);
  }

  /** If the current id is no longer among checked destinations, fall back to All. */
  private ensureSelectionIsAvailable(): void {
    if (!this._selectedDestinationId) return;
    const stillSelected = this.destinations.some(
      (d) => d.id === this._selectedDestinationId,
    );
    if (stillSelected) return;
    this._selectedDestinationId = null;
    this.selectedDestinationIdChange.emit(null);
  }
}
